import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const ContactUs: React.FC = () => {
  const { t } = useTranslation();
  // Add CSS animations
  useEffect(() => {
    const styleTag = document.getElementById('contact-animations') || document.createElement('style');
    if (!styleTag.id) {
      styleTag.id = 'contact-animations';
      styleTag.innerHTML = `
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(2deg); }
          66% { transform: translate(-20px, 20px) rotate(-2deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }
      `;
      document.head.appendChild(styleTag);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar showAnimations={false} />

      {/* Main Content */}
      <section className="pt-32 pb-0 relative overflow-hidden">
        {/* Fond animé avec gradients */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-0 -left-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0) 70%)',
              animation: 'float 20s ease-in-out infinite'
            }}
          />
          <div 
            className="absolute top-40 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, rgba(251, 146, 60, 0.3) 50%, rgba(236, 72, 153, 0) 70%)',
              animation: 'float 25s ease-in-out infinite reverse'
            }}
          />
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            {/* Badge */}
            <div 
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 backdrop-blur-sm"
              style={{
                background: '#F8F8F8',
                border: '1px solid #F0F0F0'
              }}
            >
              <div 
                className="w-2 h-2 rounded-full"
                style={{
                  background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              />
              <span className="text-sm text-gray-700 font-medium">{t('public.contact.badge')}</span>
            </div>

            {/* Titre avec gradient */}
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight px-4"
            >
              <span style={{ color: '#000000' }}>{t('public.contact.title_start')}</span>
              <span
                className="gradient-text inline-block"
                style={{
                  backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {t('public.contact.title_highlight')}
              </span>
            </h1>

            <p
              className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed px-4"
            >
              {t('public.contact.subtitle')}
            </p>
          </div>

          {/* Contact Card */}
          <div 
            className="mb-16 py-12"
            style={{ background: '#FFFFFF', width: '100vw', marginLeft: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <div 
                  className="relative rounded-3xl p-8 sm:p-12"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    border: '1px solid #F0F0F0'
                  }}
                >
                  <div className="text-center space-y-8">
                    <div>
                      <p className="text-lg sm:text-xl mb-4" style={{ color: '#4b5563' }}>
                        {t('public.contact.email_intro')}
                      </p>
                      <a 
                        href="mailto:support@focuspips.com" 
                        className="inline-block"
                        style={{ 
                          backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          fontSize: '1.5rem',
                          fontWeight: '700',
                          textDecoration: 'none',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        support@focuspips.com
                      </a>
                    </div>
                    
                    <p className="text-base sm:text-lg" style={{ color: '#4b5563' }}>
                      {t('public.contact.response_time')}
                    </p>

                    {/* Bouton CTA avec gradient */}
                    <div className="flex justify-center pt-4">
                      <a 
                        href="mailto:support@focuspips.com"
                        className="bento-button inline-flex items-center gap-3 px-8 py-4 text-base sm:text-lg font-semibold"
                        style={{
                          background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                          color: 'white',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 15px 40px rgba(139, 92, 246, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 10px 30px rgba(139, 92, 246, 0.3)';
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('public.contact.cta_email')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
